import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shortDay } from '../../../../../lib/clubwise/window';
import { axisBucketLabel, labelStride } from '../../../../../lib/portalReport/series';
import {
  anchoredDotIndexes,
  changeBetween,
  changeTone,
  chartLayout,
  dotRadius,
  gapRuns,
  lineSegments,
  partialIndexes,
  partialTreatment,
  resolveAxis,
  stackedCoverageAt,
  stackedTotalAt,
  type ChartForm,
  type ChartKind,
  type ChartMetric,
  type ChartScale,
  type ChartSeries,
} from '../../../../../lib/portalReport/chartPlan';

// CLIENT PORTAL RESTYLE — the ONE chart canvas, four instances.
//
// ⚠️⚠️ EVERY HONESTY DECISION IS MADE UPSTREAM, in chartPlan.ts. This file
// draws what the plan says and nothing more: it does not decide whether a
// series may stack, where an axis starts, what counts as a gap, or which way is
// good. That split is what lets those rules be asserted without jsdom, and it
// is why there is no arithmetic in here that a test cannot already see.
//
// ⚠️⚠️ COLOURS ARE CSS CUSTOM PROPERTIES APPLIED THROUGH `style`, NOT VALUES
// READ AT RENDER TIME. `style={{ fill: 'var(--rpt-chan-a)' }}` re-resolves
// itself when the theme attribute changes, so the chart follows a light/dark
// toggle even if React never re-renders. (It does re-render — the mode is React
// state above — but depending on that would be depending on the weaker of the
// two guarantees.) Reading `getComputedStyle` into a literal, as the mockup
// does, is the version that goes stale.
//
// ⚠️ These are FIXED data colours, never a brand accent. Snap's brand colour is
// red, and a red performance chart reads as loss on a page of results —
// dataColours.test.ts asserts this module cannot reach a theme at all.

const COLOUR_VAR: Record<ChartSeries['role'], string> = {
  a: 'var(--rpt-chan-a)',
  b: 'var(--rpt-chan-b)',
  baseline: 'var(--rpt-baseline)',
};

const W = 780;
const H = 250;
const PAD = { t: 18, r: 16, b: 34, l: 60 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

export interface TrendChartProps {
  kind: ChartKind;
  metric: ChartMetric;
  form: ChartForm;
  series: ChartSeries[];
  /** Formats a value for the axis (short) and the tooltip (full). */
  /** ⚠️ Takes the decimals the AXIS STEP needs — see `decimalsForStep`. */
  formatAxis: (v: number, scale: ChartScale) => string;
  formatValue: (v: number) => string;
  /** Appended to the bucket label in the tooltip header. */
  ariaLabel: string;
  /**
   * The dashed reference line, when one belongs (the period cost-per-lead view).
   * ⚠️ Null rather than 0 when there is no average to draw.
   */
  average?: { value: number; label: string } | null;
}

interface Hover {
  index: number;
  /** Viewport coordinates for the tooltip. */
  x: number;
  y: number;
}

export function ReportTrendChart(props: TrendChartProps) {
  const { kind, metric, form, series, formatAxis, formatValue } = props;
  const [hover, setHover] = useState<Hover | null>(null);
  // ⚠️⚠️ THE RENDERED WIDTH, BECAUSE LEGIBILITY IS A PIXEL PROPERTY (§4,
  // 2026-09-03). The SVG scales, so a label and its band scale TOGETHER and
  // their ratio tells you nothing about whether a reader can see a gap between
  // two ticks. Measured: `Sep 2025` leaves 5.4 user units of clearance in a
  // 13-month band — ~7px on a full-width chart and ~2px on a 330px CRM card,
  // which is the difference between a gap and one continuous run of text.
  //
  // ⚠️ DEFAULTS TO THE viewBox WIDTH, so the first paint is the correct
  // full-width answer rather than a blank axis. `measureBeforeMount` was
  // rejected on the staff grid for exactly that reason and the same applies
  // here.
  const [renderedWidth, setRenderedWidth] = useState(W);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width ?? 0;
      if (w > 0) setRenderedWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = chartLayout(kind, metric, form, series.length);
  const n = series[0]?.points.length ?? 0;
  const axis = useMemo(() => resolveAxis(layout, metric, form, series), [layout, metric, form, series]);
  const band = n > 0 ? IW / n : IW;

  const y = useCallback(
    (v: number) => PAD.t + IH - ((v - axis.min) / (axis.max - axis.min)) * IH,
    [axis],
  );
  const cx = useCallback((i: number) => PAD.l + band * (i + 0.5), [band]);

  const onMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const i = Math.floor(((e.clientX - rect.left) * (W / rect.width) - PAD.l) / band);
      if (i < 0 || i >= n) {
        setHover(null);
        return;
      }
      setHover({
        index: i,
        x: rect.left + cx(i) * (rect.width / W),
        // ⚠️⚠️ THE SVG'S OWN TOP EDGE, AND THAT IS THE CLAMP (§5, 2026-09-03).
        // Scott wants more of the chart visible, so the tooltip lifts out of the
        // plot area by `PAD.t` plus the old 10px gap — roughly 24px at a typical
        // scale, which is most of a row.
        //
        // ⚠️ IT CANNOT RISE FURTHER, BY CONSTRUCTION rather than by arithmetic:
        // the control row is a SIBLING ABOVE this SVG, so anchoring to
        // `rect.top` makes overlapping it impossible without measuring anything.
        // An offset tuned against the controls' height would drift the first
        // time a switch wrapped to two lines.
        //
        // ⚠️ AND IT CANNOT GO ABOVE THE PLOT ENTIRELY. `PAD.t` is 18 of a
        // 250-unit viewBox — about 24px on screen — and a two-row tooltip is
        // ~60px, so "flip above the cursor when there is room" has no room to
        // find here. Measured before choosing, not assumed.
        y: rect.top,
      });
    },
    [band, cx, n],
  );

  if (n === 0 || series.every((s) => s.points.every((p) => p.value === null))) {
    return (
      <p className="rpt-muted" style={{ margin: '28px 0', fontSize: 14 }}>
        Nothing was measured in this period.
      </p>
    );
  }

  const base = y(axis.min);
  const gaps = gapRuns(series[0]?.points ?? []);
  const treatment = partialTreatment(layout);

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={props.ariaLabel}
        style={{ display: 'block', width: '100%', height: 'auto', overflow: 'visible' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {/*
            ⚠️ HATCHING MEANS EXACTLY ONE THING HERE: "this period is
            incomplete." It briefly also meant "Google" in the mockup, and every
            COMPLETE month then read as provisional.
          */}
          <pattern
            id="rpt-hatch"
            width="6"
            height="6"
            patternTransform="rotate(45)"
            patternUnits="userSpaceOnUse"
          >
            <rect width="6" height="6" fillOpacity={0.22} style={{ fill: COLOUR_VAR.a }} />
            <line x1="0" y1="0" x2="0" y2="6" strokeWidth={2.5} style={{ stroke: COLOUR_VAR.a }} />
          </pattern>
        </defs>

        {/* Gridlines + a real, nice-rounded scale — a chart with no axis forces
            every question through the tooltip. */}
        {axis.ticks.map((v) => (
          <g key={v}>
            <line
              x1={PAD.l}
              y1={y(v)}
              x2={PAD.l + IW}
              y2={y(v)}
              strokeOpacity={v === axis.min ? 0.3 : 0.13}
              style={{ stroke: 'var(--rpt-muted)' }}
            />
            <text
              x={PAD.l - 9}
              y={y(v) + 3.5}
              textAnchor="end"
              className="rpt-axis-label"
              style={{ fill: 'var(--rpt-muted)' }}
            >
              {formatAxis(v, axis)}
            </text>
          </g>
        ))}

        {/* ⚠️ AN UNMEASURED SPAN IS NAMED, NEVER ZEROED. */}
        {gaps.map((g) => {
          const gx = PAD.l + band * g.from;
          const gw = band * (g.to - g.from + 1);
          return (
            <g key={`gap-${g.from}`}>
              <rect
                x={gx}
                y={PAD.t}
                width={gw}
                height={IH}
                fillOpacity={0.13}
                style={{ fill: 'var(--rpt-muted)' }}
              />
              {gw > 70 && (
                <text
                  x={gx + gw / 2}
                  y={PAD.t + IH / 2}
                  textAnchor="middle"
                  className="rpt-axis-label"
                  style={{ fill: 'var(--rpt-muted)' }}
                >
                  NOT MEASURED
                </text>
              )}
            </g>
          );
        })}

        {layout === 'stacked' && (
          <StackedBars series={series} band={band} baseY={base} y={y} treatment={treatment} />
        )}
        {layout === 'paired' && <PairedStacks series={series} band={band} baseY={base} y={y} />}
        {(layout === 'grouped' || layout === 'single') && (
          <PlainBars
            series={series}
            band={band}
            baseY={base}
            y={y}
            cx={cx}
            treatment={treatment}
            grouped={layout === 'grouped'}
          />
        )}
        {layout === 'line' && <Lines series={series} band={band} y={y} cx={cx} />}

        {props.average != null && (
          <g>
            <line
              x1={PAD.l}
              y1={y(props.average.value)}
              x2={PAD.l + IW}
              y2={y(props.average.value)}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              style={{ stroke: 'var(--rpt-muted)' }}
            />
            <text
              x={PAD.l + 7}
              y={y(props.average.value) - 8}
              className="rpt-axis-label"
              style={{ fill: 'var(--rpt-muted)' }}
            >
              {props.average.label}
            </text>
          </g>
        )}

        {/*
          Bucket labels, thinned so two of them can never read as one run of
          text (§3/§4, 2026-09-03).

          ⚠️⚠️ THE SHORT FORM DOES NOT FIX A COLLISION — IT POSTPONES ONE.
          Measured: `Sep ’25` is only 13% narrower than `Sep 2025`. Scott's
          screenshot of the CRM cards read `Sep 2025Oct 2025Nov 2025…`, and
          measuring it showed the labels were not even overlapping — they had
          5.4 user units of clearance, which is ~7px on a full-width chart and
          ~2px on a 330px card. So the stride is computed from the RENDERED
          SCALE, not from the band alone.

          ⚠️ `axisBucketLabel` shortens MONTHS and leaves DAYS alone — a daily
          bucket is already `3 Sep`. Two date formats on one report is worse
          than one long one, so both go through the one function.
        */}
        {series[0]?.points.map((p, i) => {
          const every = labelStride(series[0]?.points ?? [], band, renderedWidth / W);
          if (i % every !== 0) return null;
          return (
            <text
              key={`x-${i}`}
              x={cx(i)}
              y={H - 11}
              textAnchor="middle"
              className="rpt-axis-label"
              style={{ fill: 'var(--rpt-muted)' }}
            >
              {axisBucketLabel(p)}
            </text>
          );
        })}

        {/* ⚠️ THE HOVER TARGET IS THE WHOLE COLUMN, not a 3px mark — which is
            also why the dot-density rule can drop dots without costing anyone a
            hit target. */}
        {hover && (
          <rect
            x={PAD.l + band * hover.index}
            y={PAD.t}
            width={band}
            height={IH}
            rx={4}
            style={{ fill: 'var(--rpt-chip)' }}
          />
        )}
        {/* ⚠️ THE HOVERED POINT ALWAYS GETS A DOT, so a single day stays
            identifiable in a 90-day line. */}
        {hover &&
          layout === 'line' &&
          series.map((s) => {
            const v = s.points[hover.index]?.value;
            if (v === null || v === undefined) return null;
            return (
              <circle
                key={`hd-${s.label}`}
                cx={cx(hover.index)}
                cy={y(v)}
                r={4.5}
                strokeWidth={2.5}
                style={{ fill: 'var(--rpt-card)', stroke: COLOUR_VAR[s.role] }}
              />
            );
          })}
      </svg>

      {hover && (
        <ChartTooltip
          hover={hover}
          series={series}
          layout={layout}
          metric={metric}
          formatValue={formatValue}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marks
// ---------------------------------------------------------------------------

interface MarkProps {
  series: ChartSeries[];
  band: number;
  baseY: number;
  y: (v: number) => number;
}

/**
 * ⚠️ Only where the parts genuinely sum to the whole. `chartLayout` is what
 * decides that; this component never asks.
 */
function StackedBars({
  series,
  band,
  baseY,
  y,
  treatment,
}: MarkProps & { treatment: ReturnType<typeof partialTreatment> }) {
  const bw = Math.min(46, band * 0.62);
  const n = series[0]?.points.length ?? 0;
  const partial = new Set(partialIndexes(series[0]?.points ?? []));
  const out: React.ReactNode[] = [];
  for (let i = 0; i < n; i += 1) {
    const coverage = stackedCoverageAt(series, i);
    // Nothing measured at all: the shaded NOT MEASURED band already says so, and
    // a column of zero height would say something else.
    if (coverage === 'none') continue;
    const x = PAD.l + band * (i + 0.5) - bw / 2;
    let top = baseY;
    const isPartial = partial.has(i);
    // ⚠️⚠️ SOME MEASURED, SOME NOT. The segments we HAVE are drawn — a month with
    // real Meta spend must appear, and the Line form has always drawn it — but
    // the column's top is then not the bucket's total, and a stacked column's
    // height reads as exactly that. See `unknownTop` below.
    const unknownRemainder = coverage === 'partial';
    series.forEach((s, k) => {
      const p = s.points[i];
      if (!p || p.coverage === 'no-data' || p.value === null) return;
      const h = baseY - y(p.value);
      if (h <= 0) return;
      // A 2px surface gap, so the channel boundary is a real edge not a guess.
      const gap = k === 0 ? 0 : 2;
      out.push(
        <rect
          key={`sb-${i}-${k}`}
          x={x}
          y={top - h - gap}
          width={bw}
          height={Math.max(1, h)}
          rx={3}
          // ⚠️ A partial bucket FADES; it does not hatch. This chart already
          // carries two fills, and a texture would compete with the split.
          fillOpacity={isPartial && treatment === 'fade' ? 0.55 : 1}
          style={{ fill: COLOUR_VAR[s.role] }}
        />,
      );
      top = top - h - gap;
    });
    if (unknownRemainder) {
      out.push(unknownTop(i, x, top, bw, COLOUR_VAR[series.at(-1)?.role ?? 'a']));
    } else if (isPartial) {
      // ⚠️ ONLY when the column is otherwise complete. A dashed straight cap
      // already means "this period is still running" on this chart, so drawing
      // it alongside the torn edge would put two caps at one height and give the
      // dash a second meaning — the double-meaning the hatch was rescued from.
      // A partial-period bucket that is ALSO missing a channel keeps its faded
      // fill, which is what carries "still running" here.
      out.push(
        <line
          key={`cap-${i}`}
          x1={x}
          y1={top}
          x2={x + bw}
          y2={top}
          strokeWidth={2}
          strokeDasharray="3 3"
          style={{ stroke: COLOUR_VAR[series.at(-1)?.role ?? 'a'] }}
        />,
      );
    }
  }
  return <>{out}</>;
}

/**
 * ⚠️⚠️ A TORN TOP EDGE — "this column is not the whole story".
 *
 * The constraint: a stacked column's HEIGHT reads as the bucket's total, so a
 * column drawn from Meta alone asserts a total that Google's unmeasured month
 * makes unknowable. That is a real fabrication, and the `continue` this replaces
 * was somebody preventing it. But the data IS showable — the Line form has
 * always drawn this bucket — so the answer is to draw it and mark the top.
 *
 * ⚠️⚠️ WHY A ZIGZAG AND NOT SOMETHING SIMPLER: every simpler signal on this
 * chart is already spoken for. The HATCH means "incomplete period" and nothing
 * else — giving it a second meaning is the exact double-meaning Scott caught on
 * 2026-08-30, when complete months read as provisional. On the STACKED layout
 * specifically, the fade AND the dashed straight cap both already mean
 * "incomplete period" too. So the remaining channel is SHAPE, and a torn edge is
 * the one shape that already reads as "cut off, continues beyond here" — the
 * broken-axis convention, at column scale.
 *
 * ⚠️ It is a 2px stroke in the topmost segment's own colour, not a new hue: a
 * third colour on a two-channel chart would read as a third channel.
 *
 * ⚠️ ONLY for `coverage === 'partial'`. A channel at a MEASURED ZERO means the
 * other one IS the total and the column is complete — no mark.
 */
function unknownTop(
  index: number,
  x: number,
  top: number,
  width: number,
  stroke: string,
): React.ReactNode {
  // Four teeth across the column, drawn just above the last segment so the
  // segment's own top edge stays readable underneath it.
  const teeth = 4;
  const step = width / teeth;
  const amp = 3;
  const y0 = top - 3;
  let d = `M${x.toFixed(1)} ${y0.toFixed(1)}`;
  for (let t = 0; t < teeth; t += 1) {
    const midX = x + step * (t + 0.5);
    const endX = x + step * (t + 1);
    d += `L${midX.toFixed(1)} ${(y0 - amp).toFixed(1)}L${endX.toFixed(1)} ${y0.toFixed(1)}`;
  }
  return (
    <path
      key={`torn-${index}`}
      d={d}
      fill="none"
      strokeWidth={2}
      strokeLinejoin="round"
      strokeLinecap="round"
      style={{ stroke }}
    />
  );
}

/**
 * "Share of each": two 100% stacks per bucket — the spend split, then the lead
 * split. ⚠️ The ONLY view where two different measures appear together, and
 * only because both are then percentages of their OWN total.
 */
function PairedStacks({ series, band, baseY, y }: MarkProps) {
  const bw = (band - 12) / 2;
  const showSub = band >= 34;
  const n = series[0]?.points.length ?? 0;
  const out: React.ReactNode[] = [];
  const SUB = ['SPD', 'LDS'];
  for (let i = 0; i < n; i += 1) {
    series.forEach((s, k) => {
      const pct = s.points[i]?.value;
      if (pct === null || pct === undefined) return;
      const x = PAD.l + band * i + 6 + k * (bw + 2);
      const lowerH = baseY - y(pct);
      const upperH = baseY - y(100) - lowerH;
      out.push(
        <rect
          key={`ps-a-${i}-${k}`}
          x={x}
          y={baseY - lowerH}
          width={bw}
          height={lowerH}
          rx={3}
          style={{ fill: COLOUR_VAR.a }}
        />,
        <rect
          key={`ps-b-${i}-${k}`}
          x={x}
          y={baseY - lowerH - 2 - upperH}
          width={bw}
          height={Math.max(1, upperH)}
          rx={3}
          style={{ fill: COLOUR_VAR.b }}
        />,
      );
      if (showSub) {
        out.push(
          <text
            key={`ps-l-${i}-${k}`}
            x={x + bw / 2}
            y={baseY + 11}
            textAnchor="middle"
            fontSize={8}
            className="rpt-axis-label"
            style={{ fill: 'var(--rpt-muted)' }}
          >
            {SUB[k]}
          </text>,
        );
      }
    });
  }
  return <>{out}</>;
}

function PlainBars({
  series,
  band,
  baseY,
  y,
  cx,
  treatment,
  grouped,
}: MarkProps & {
  cx: (i: number) => number;
  treatment: ReturnType<typeof partialTreatment>;
  grouped: boolean;
}) {
  const groupW = band - 8;
  const bw = grouped ? (groupW - 3) / 2 : Math.min(46, groupW);
  const out: React.ReactNode[] = [];
  series.forEach((s, k) => {
    const partial = new Set(partialIndexes(s.points));
    s.points.forEach((p, i) => {
      if (p.value === null) return;
      const h = Math.max(1, baseY - y(p.value));
      const x = grouped ? PAD.l + band * i + 4 + k * (bw + 3) : cx(i) - bw / 2;
      const isPartial = partial.has(i) && treatment === 'hatch';
      out.push(
        <rect
          key={`b-${k}-${i}`}
          x={x}
          y={y(p.value)}
          width={bw}
          height={h}
          rx={3}
          strokeWidth={isPartial ? 1.5 : 0}
          fill={isPartial ? 'url(#rpt-hatch)' : undefined}
          style={{
            ...(isPartial ? {} : { fill: COLOUR_VAR[s.role] }),
            stroke: isPartial ? COLOUR_VAR[s.role] : 'none',
          }}
        />,
      );
    });
  });
  return <>{out}</>;
}

function Lines({
  series,
  band,
  y,
  cx,
}: Omit<MarkProps, 'baseY'> & { cx: (i: number) => number }) {
  const r0 = dotRadius(band);
  const out: React.ReactNode[] = [];
  series.forEach((s, k) => {
    const partial = new Set(partialIndexes(s.points));
    const anchored = new Set(anchoredDotIndexes(s.points));
    // ⚠️ THE LINE BREAKS ACROSS AN UNMEASURED SPAN AND ONLY THERE. Within a
    // segment it connects across a null, because a measured bucket with spend
    // and no leads is a KNOWN state — breaking there looks like a bug to a
    // reader who can see data either side.
    lineSegments(s.points).forEach((seg, si) => {
      const pts: Array<[number, number]> = [];
      for (let i = seg.from; i <= seg.to; i += 1) {
        const v = s.points[i]?.value;
        if (v === null || v === undefined) continue;
        pts.push([cx(i), y(v)]);
      }
      if (pts.length < 2) return;
      // ⚠️ A partial FINAL point makes its own segment dashed, so the reader
      // can see where the complete data stops.
      const endsPartial = partial.has(seg.to);
      const body = endsPartial ? pts.slice(0, -1) : pts;
      if (body.length > 1) {
        out.push(
          <path
            key={`l-${k}-${si}`}
            d={`M${body.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join('L')}`}
            fill="none"
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray={s.role === 'baseline' ? '4 4' : undefined}
            style={{ stroke: COLOUR_VAR[s.role] }}
          />,
        );
      }
      if (endsPartial && pts.length > 1) {
        const a = pts[pts.length - 2] as [number, number];
        const b = pts[pts.length - 1] as [number, number];
        out.push(
          <path
            key={`ld-${k}-${si}`}
            d={`M${a[0].toFixed(1)} ${a[1].toFixed(1)}L${b[0].toFixed(1)} ${b[1].toFixed(1)}`}
            fill="none"
            strokeWidth={2}
            strokeDasharray="3 3"
            style={{ stroke: COLOUR_VAR[s.role] }}
          />,
        );
      }
    });
    s.points.forEach((p, i) => {
      if (p.value === null) return;
      const isPartial = partial.has(i);
      // ⚠️ The last measured point is ALWAYS marked — it is the number the
      // reader came for, and an unmarked line end reads as a cropped chart.
      const r = isPartial ? 4.2 : anchored.has(i) ? 3.2 : r0;
      if (!r) return;
      out.push(
        <circle
          key={`d-${k}-${i}`}
          cx={cx(i)}
          cy={y(p.value)}
          r={r}
          strokeWidth={r > 2.5 ? 2 : 1.4}
          style={{
            // A hollow dot marks the partial point.
            fill: isPartial ? 'var(--rpt-card)' : COLOUR_VAR[s.role],
            stroke: COLOUR_VAR[s.role],
          }}
        />,
      );
    });
  });
  return <>{out}</>;
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

/**
 * ⚠️ SYMMETRICAL WHERE IT MATTERS. Every series that IS labelled gets a row of
 * the same shape — the mockup collapsed Google into a combined "37.0% / 35.1%"
 * row while Meta got a line each, which makes the reader do the parsing.
 *
 * ⚠️⚠️ BUT A SINGLE SERIES GETS NO LABEL ROW AT ALL (§5, 2026-09-03). "Last 12
 * months" beside the only number on the card is a LEGEND, not information, and
 * it was the widest line in the box; the swatch goes with it, because a swatch
 * distinguishing one thing from nothing distinguishes nothing. TWO series keep
 * their rows — there the label is the only thing telling 2026 from 2025.
 *
 * ⚠️⚠️ IT NO LONGER SITS INSIDE THE PLOT, AND THAT REVERSES A STATED REASON.
 * This comment used to read: "It sits INSIDE the plot (`translate(-50%, 10px)`).
 * Anchored above, it covers the control row — which is exactly where the
 * reader's hand already is." The hazard was real and is NOT dismissed; Scott
 * asked for more chart visible, so the tooltip lifts to the SVG's own top edge
 * and CANNOT rise past it. The control row is a sibling above that edge, so the
 * old failure is now structurally impossible rather than merely avoided — see
 * the anchor in `onMove`.
 */
function ChartTooltip({
  hover,
  series,
  layout,
  metric,
  formatValue,
}: {
  hover: Hover;
  series: ChartSeries[];
  layout: ReturnType<typeof chartLayout>;
  metric: ChartMetric;
  formatValue: (v: number) => string;
}) {
  const i = hover.index;
  const head = series[0]?.points[i];
  const isPartial = head?.coverage === 'in-progress';
  const total = layout === 'stacked' ? stackedTotalAt(series, i) : null;
  // ⚠️ A change row only where the two series are the SAME measure over two
  // runs of time. Comparing Meta to Google is not a "change".
  const change =
    series.length === 2 && series[1]?.role === 'baseline'
      ? changeBetween(series[0]?.points[i]?.value ?? null, series[1]?.points[i]?.value ?? null)
      : null;
  const tone = changeTone(metric, change);

  return (
    <div
      className="rpt-tooltip"
      style={{
        position: 'fixed',
        left: hover.x,
        top: hover.y,
        transform: 'translate(-50%, 10px)',
        pointerEvents: 'none',
        zIndex: 60,
      }}
    >
      <div className="rpt-tooltip-head">
        {head?.label}
        {head?.partialTo ? ` · to ${shortDay(head.partialTo)}` : isPartial ? ' · partial' : ''}
      </div>
      <dl className="rpt-tooltip-rows">
        {series.map((s) => {
          const v = s.points[i]?.value;
          const value = v === null || v === undefined ? 'not measured' : formatValue(v);
          // ⚠️ ONE SERIES: the value alone. Its label is a legend and its swatch
          // separates it from nothing. See the component's header.
          if (series.length === 1) {
            return (
              <div key={s.label} className="rpt-tooltip-row rpt-tooltip-solo">
                {/* ⚠️ "not measured", never a zero. */}
                <dd>{value}</dd>
              </div>
            );
          }
          return (
            <div key={s.label} className="rpt-tooltip-row">
              <dt>
                <span className="rpt-swatch" style={{ background: COLOUR_VAR[s.role] }} />
                {s.label}
              </dt>
              {/* ⚠️ "not measured", never a zero. */}
              <dd>{value}</dd>
            </div>
          );
        })}
        {total !== null && (
          <div className="rpt-tooltip-row rpt-tooltip-total">
            <dt>Total</dt>
            <dd>{formatValue(total)}</dd>
          </div>
        )}
        {change !== null && (
          <div className="rpt-tooltip-row rpt-tooltip-total">
            <dt>Change</dt>
            {/* ⚠️⚠️ THE TONE INVERTS FOR COST METRICS — decided by `changeTone`,
                never by the sign here. Getting it backwards tells the client the
                opposite of the truth on the one number they most want to fall. */}
            <dd className={`rpt-tone-${tone}`}>
              {change > 0 ? '+' : ''}
              {change.toFixed(1)}%
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}
